import React, { useCallback, useMemo } from 'react';

import UploadDocumentsModal from 'app/shared/components/document/UploadDocumentsModal';
import clsx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { DOCUMENT_FILE_TYPE } from 'constants/file';

import MissingDocumentType from './MissingDocumentType';

const UPLOAD_FILE_ALLOWED_FORMATS = [
  DOCUMENT_FILE_TYPE.PDF,
  DOCUMENT_FILE_TYPE.PNG,
  DOCUMENT_FILE_TYPE.JPEG,
];

const MissingDocumentTypesTab = ({
  loanParams,
  missingDocuments,
  setGenerateBundleState,
  chosenMissingDocumentTypeId,
  setChosenMissingDocumentTypeId,
  abortController,
  formRef = null,
}) => {
  const missingDocTypesStyles = {
    width: '300px',
    height: '340px',
  };
  const classes = {
    missingDocTypes: missingDocTypesStyles,
  };

  const chosenMissingDocumentType = useMemo(
    () => missingDocuments.find(({ docTypeId }) => docTypeId === chosenMissingDocumentTypeId),
    [chosenMissingDocumentTypeId, missingDocuments],
  );

  const updateMissingDocumentTypes = useCallback(
    (docTypeId, uploadedFiles, isFulfilled) => {
      const updatedMissingDocuments = missingDocuments.map(documentType => {
        if (documentType.docTypeId === docTypeId) {
          const isReady = documentType.isReady || isFulfilled;

          return {
            docTypeId,
            isReady,
            uploadedFiles: _.uniqBy(documentType.uploadedFiles.concat(...uploadedFiles), 'name'),
          };
        }

        return documentType;
      });

      const hasMissingDocuments = updatedMissingDocuments.some(({ isReady }) => !isReady);

      setGenerateBundleState(state => ({
        ...state,
        hasMissingDocuments,
        missingDocuments: updatedMissingDocuments,
        error: hasMissingDocuments ? 'You have missing documents' : '',
      }));
    },
    [setGenerateBundleState, missingDocuments],
  );

  return (
    <div className="flex justify-center">
      <div className="flex">
        <div className={clsx(classes.missingDocTypes, 'mt-24 overflow-y-auto')}>
          {missingDocuments.map(({ docTypeId, isReady }) => (
            <MissingDocumentType
              documentTypeId={docTypeId}
              isChosen={docTypeId === chosenMissingDocumentTypeId}
              isReady={isReady}
              key={docTypeId}
              setChosenMissingDocumentTypeId={setChosenMissingDocumentTypeId}
            />
          ))}
        </div>
        {chosenMissingDocumentTypeId && (
          <UploadDocumentsModal
            abortController={abortController}
            documentTypeId={chosenMissingDocumentTypeId}
            formRef={formRef}
            isModal={false}
            loanParams={loanParams}
            missingUploadedFiles={chosenMissingDocumentType?.uploadedFiles}
            updateMissingDocumentTypes={updateMissingDocumentTypes}
            uploadFileAllowedFormats={UPLOAD_FILE_ALLOWED_FORMATS}
          />
        )}
      </div>
    </div>
  );
};

MissingDocumentTypesTab.propTypes = {
  loanParams: PropTypes.object.isRequired,
  abortController: PropTypes.object.isRequired,
  chosenMissingDocumentTypeId: PropTypes.string.isRequired,
  missingDocuments: PropTypes.array.isRequired,
  setGenerateBundleState: PropTypes.func.isRequired,
  setChosenMissingDocumentTypeId: PropTypes.func.isRequired,
  formRef: PropTypes.shape({ current: PropTypes.object }),
};

export default MissingDocumentTypesTab;
